/* eslint-disable no-undefined */
import React, { useState } from "react";
import { AiOutlineCalendar } from "react-icons/ai";
import { HiOutlineMinus, HiOutlinePlus } from "react-icons/hi";

// components
import Dropdown, { DropdownMenuItemProps } from "@/shared/Dropdown";
import SpaceSizeDropdown from "@/components/SpaceSizeDropdown";
import WhenDropdownPicker from "@/components/WhenDropdownPicker";
import BudgetRangeDropdown from "@/components/BudgetRangeDropdown";
import Button from "@/shared/Button";
import Collapse from "@/components/Collapse";

// helpers
import { SPACE_SIZES } from "@/components/SpaceSizeDropdown/constants";
import useQueryParams from "@/hooks/useQueryParams";
import { SPACE_TYPES, IAM_FLEXIBLE_OPTION, IAM_FLEXIBLE_KEY } from "@/globals/global.constants";
import currencyUtils from "@/helpers/currencyUtils";
import { useLocationSpacesContainerFiltersData } from "@/pageComponents/SingleLocationPage/hooks";
import useCurrency from "@/hooks/useCurrency";
import { useDimension } from "@/hooks/useDimensions";
import dayjs from "@/helpers/dayjs";

// assets
import AdvancedFilterIcon from "@/components/icons/AdvancedFilterIcon";
import styles from "@/assets/styles/pages/location-single-page/location-single-page-components.module.scss";


type SelectedValue = {
  label: string;
  value: string;
  isClosable?: boolean;
}[];

type TBudgetValue = {
  min: number | null;
  max: number | null;
};

type IHandleSpaceSizeChangeValue = SelectedValue | { width: number | string | null; length: number | string | null };
interface ILocationSpacesContainerFilters {
  isRestricted?: boolean;
  onRestrictedButtonClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const LocationSpacesContainerFilters = (props: ILocationSpacesContainerFilters) => {
  const { isRestricted, onRestrictedButtonClick } = props;

  const [isShowAllFilters, setIsShowAllFilters] = useState(false);

  const [params, setParams] = useQueryParams();
  const [currency] = useCurrency();
  const [width] = useDimension();

  const isTablesView = !!width && width <= 920;

  const {
    currencyOptions,
    purposesOptions
  } = useLocationSpacesContainerFiltersData();

  const {
    purpose__in,
    type__in,
    rate_daily__range,
    width__gte,
    length__gte,
    size__in,
    start_date,
    end_date
  } = params || {};

  const typeIn = type__in?.split("__").map((item: string) => +item);

  const isAnyFilterSelected = [
    purpose__in,
    type__in,
    rate_daily__range,
    width__gte,
    length__gte,
    size__in,
    start_date,
    end_date,
  ].some(Boolean);

  const spaceTypeOptions = [...SPACE_TYPES, IAM_FLEXIBLE_OPTION];
  const selectedTypeIn = (typeof typeIn === "undefined")
    ? [IAM_FLEXIBLE_OPTION]
    : [
      typeIn.length === SPACE_TYPES.length && IAM_FLEXIBLE_OPTION,
      ...spaceTypeOptions.filter(item => typeIn.includes(item.value as number)),
    ].filter(Boolean);

  const selectedCurrency = currencyOptions.find(item => item.value === (currency));
  const selectedPurpose = purposesOptions.find(item => item.value === +(purpose__in as string));
  const selectedSpaceSize = SPACE_SIZES.filter(item => size__in?.includes(item.value));

  const currencySymbol = currencyUtils.getCurrencySymbol(selectedCurrency?.value as string);

  // methods
  const handleChange = (key: string, value: unknown) => {
    setParams({ [key]: value });
  };

  const handleChangeSpaceType = (e: DropdownMenuItemProps[], key: string) => {
    const isFlexibleSelected = e.find((item: DropdownMenuItemProps) => item.value === IAM_FLEXIBLE_KEY);
    const selectedItems = e.filter((item: DropdownMenuItemProps) => item.value !== IAM_FLEXIBLE_KEY);

    if (selectedItems.length === SPACE_TYPES.length && key === IAM_FLEXIBLE_KEY) {
      handleChange("type__in", null);

      return;
    } else if (key === IAM_FLEXIBLE_KEY && isFlexibleSelected) {
      handleChange("type__in", SPACE_TYPES.map(item => item.value).join("__"));

      return;
    }

    handleChange("type__in", selectedItems.map((item: DropdownMenuItemProps) => item.value).join("__"));
  };

  const handleDateChange = (e: [dayjs.Dayjs | null, dayjs.Dayjs | null, number | null]) => {
    const [startDate, endDate] = e;

    setParams({
      start_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      end_date: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
    });
  };

  const handleSpaceSizeChange = (e: IHandleSpaceSizeChangeValue, key?: string) => {
    if (key === "custom" && !Array.isArray(e)) {
      setParams({ size__in: null, width__gte: e.width, length__gte: e.length, });

      return;
    }

    setParams({
      size__in: (e as SelectedValue).map((item) => item.value).join("__"),
      width__gte: null,
      length__gte: null,
    });
  };

  const handelBudgetChange = (e: TBudgetValue) => {
    const [min, max] = [e.min || 0, e.max || null];
    const isMaxNumber = typeof e.max === "number" && !isNaN(e.max);

    const listToSet = [
      min && max && min > max ? max : min,
      min && max && min > max ? min : max,
    ].filter(item => typeof item === "number");

    const valueToSet = isMaxNumber ? listToSet.join("__") : null;

    if (valueToSet) {
      setParams({ rate_daily__range: valueToSet });
    }
  };

  const handleResetFilters = () => {
    setParams({
      purpose__in: null,
      type__in: null,
      rate_daily__range: null,
      width__gte: null,
      length__gte: null,
      size__in: null,
      start_date: null,
      end_date: null,
    });
  };

  const getRateDailyRange = () => {
    if (!rate_daily__range) {
      return { min: null, max: null };
    }

    const [min, max] = rate_daily__range.split("__");

    return { min: +min, max: +max || null };
  };

  const handleCalculateSelectedFilter = () => {
    let numberOfSelectedFilters = 0;

    if (params?.purpose__in) {
      numberOfSelectedFilters++;
    }

    if (params?.type__in) {
      numberOfSelectedFilters++;
    }

    if (params?.rate_daily__range) {
      numberOfSelectedFilters++;
    }

    if (params?.width__gte || params?.length__gte || params?.size__in) {
      numberOfSelectedFilters++;
    }

    if (params?.start_date || params?.end_date) {
      numberOfSelectedFilters++;
    }

    return numberOfSelectedFilters;
  }

  const handleMoreClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestricted) {
      onRestrictedButtonClick && onRestrictedButtonClick(e);

      return;
    }

    setIsShowAllFilters(prev => !prev);
  }

  const handleFilterButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestricted) {
      onRestrictedButtonClick && onRestrictedButtonClick(e);
    }
  }

  // renders
  const renderClearFiltersButton = () => {
    if (!isAnyFilterSelected) {
      return null;
    }

    return (
      <Button
        success
        type="tertiary"
        onClick={handleResetFilters}
        data-testid="locl-location-spaces-container-filters-button-clear"
        className={styles["locl-location-page-spaces-container-filters__item__clear"]}
      >
        Clear filters ({ handleCalculateSelectedFilter() })
      </Button>
    );
  };

  const renderFields = () => (<>
    <Dropdown
      placeholder="Use of Space"
      menu={{ items: purposesOptions }}
      value={selectedPurpose}
      onChange={(e) => handleChange("purpose__in", (e as DropdownMenuItemProps).value)}
      buttonProps={{
        "data-testid": "locl-location-spaces-container-filters-purposes",
        className: styles["locl-location-page-spaces-container-filters__item__purpose__button"]
      }}
      className={styles["locl-location-page-spaces-container-filters__item__purpose"]}
      getPopupContainer={(el) => el.parentElement as HTMLElement}
    />
    <WhenDropdownPicker
      onChange={handleDateChange}
      defaultValue={
        [params.start_date, params.end_date].map(item => item
          ? dayjs(item)
          : null
        ) as [dayjs.Dayjs | null, dayjs.Dayjs | null]}
      className={styles["locl-location-page-spaces-container-filters__item__when"]}
      overlayClassName={styles["locl-location-page-spaces-container-filters__item__when__overlay"]}
    >
      <Dropdown.Button
        placeholder="When"
        value={[params.start_date, params.end_date].filter(Boolean).map(item => dayjs(item).format("DD/MM/YYYY")).join(" - ")}
        data-testid="locl-location-spaces-container-filters-when-dropdown"
        icon={<AiOutlineCalendar style={{ flex: "0 0 20px" }}/>}
        onClick={handleFilterButtonClick}
      />
    </WhenDropdownPicker>
    { (isShowAllFilters || isTablesView) && <Dropdown
      isMulti
      placeholder="Space type"
      menu={{ items: spaceTypeOptions }}
      value={selectedTypeIn as DropdownMenuItemProps[]}
      onChange={(value, key) => handleChangeSpaceType(value as DropdownMenuItemProps[], key)}
      buttonProps={{ "data-testid": "locl-location-spaces-container-filters-space-type" }}
      className={styles["locl-location-page-spaces-container-filters__item__space-type"]}
      getPopupContainer={(el) => el.parentElement as HTMLElement}
    /> }
    { (isShowAllFilters || isTablesView) && <SpaceSizeDropdown
      onChange={handleSpaceSizeChange}
      value={selectedSpaceSize}
      customSizes={
        width__gte || length__gte
          ? { width: width__gte, length: length__gte } as unknown as { width: number | null, length: number | null }
          : undefined
      }
      className={styles["locl-location-page-spaces-container-filters__item__space-size"]}
      buttonProps={{ "data-testid": "locl-location-spaces-container-filters-space-size" }}
      getPopupContainer={(el) => el.parentElement as HTMLElement}
    /> }
    { (isShowAllFilters || isTablesView) && <BudgetRangeDropdown
      currencySymbol={currencySymbol}
      onChange={handelBudgetChange}
      value={getRateDailyRange()}
      className={styles["locl-location-page-spaces-container-filters__item__budget"]}
      buttonProps={{ "data-testid": "locl-location-spaces-container-filters-budget" }}
      getPopupContainer={(el) => el.parentElement as HTMLElement}
    /> }
    { isTablesView && renderClearFiltersButton() }
  </>)

  const renderBlock = () => (<>
    <div className={styles["locl-location-page-spaces-container-filters__list"]}>
      { renderFields() }
      <Button
        type="tertiary"
        icon={isShowAllFilters ? <HiOutlineMinus /> : <HiOutlinePlus />}
        onClick={handleMoreClick}
        className={styles["locl-location-page-spaces-container-filters__more"]}
        data-testid="locl-location-spaces-container-filters-toggle"
      >
        { isShowAllFilters ? "Less" : "More" }
      </Button>
    </div>
    { !isTablesView && renderClearFiltersButton() }
  </>)

  const renderCollapseHeader = () => {
    const badge = handleCalculateSelectedFilter();

    return (
      <div
        className={styles["locl-location-page-spaces-container-filters__collapse__header"]}
        data-testid="locl-location-spaces-container-filters-collapse-header"
      >
        <span>Filter</span>
        <AdvancedFilterIcon className={styles["locl-location-page-spaces-container-filters__collapse__header__icon"]} />
        { !!badge && (
          <span
            className={styles["locl-location-page-spaces-container-filters__collapse__header__badge"]}
            data-testid="locl-location-spaces-container-filters-collapse-badge"
          >
            { +badge }
          </span>
        ) }
      </div>
    )
  }

  const renderCollapse = () => (
    <Collapse
      defaultActiveKey={[]}
      className={styles["locl-location-page-spaces-container-filters__collapse"]}
    >
      <Collapse.Panel header={renderCollapseHeader()} key="1">
        { renderFields() }
      </Collapse.Panel>
    </Collapse>
  );

  return (
    <div className={styles["locl-location-page-spaces-container-filters"]} data-testid="locl-location-spaces-container-filters">
      {
        !isTablesView
          ? renderBlock()
          : renderCollapse()
      }
    </div>
  )
}

export default LocationSpacesContainerFilters;
